import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { AnimationMode } from "@/lib/types";
import TypewriterText from "./TypewriterText";
import EnvelopeReveal from "./EnvelopeReveal";
import DramaticReveal from "./DramaticReveal";

/**
 * Aplica el efecto de *reveal* del modo de animación sobre el cuerpo de la
 * invitación.
 *
 * Espejo web del `AnimationModeReveal` de iOS. Centraliza los modos que afectan
 * a la tarjeta, no a los botones:
 *  - `countdownReveal`: una cuenta atrás breve antes de mostrar el contenido.
 *  - `typewriterReveal`: título/mensaje escritos letra a letra.
 *  - `envelopeReveal`: el cuerpo se envuelve en un sobre que se abre al tocar.
 *  - `dramaticReveal`: el cuerpo se oculta tras un teaser hasta "Reveal".
 * El resto de modos cae en `default`, que muestra `children` tal cual.
 *
 * Los textos `title`/`message` se pasan aparte porque el modo typewriter los
 * reemplaza por `TypewriterText`; el `children` es el cuerpo completo (saludo,
 * título, mensaje, firma) para los demás modos.
 *
 * Añadir un modo = un caso más en el switch.
 */
export interface ModeRevealProps {
  mode: AnimationMode;
  title: string;
  message: string;
  children: ReactNode;
}

export default function ModeReveal({
  mode,
  title,
  message,
  children
}: ModeRevealProps) {
  switch (mode) {
    case "countdownReveal":
      return <CountdownReveal>{children}</CountdownReveal>;
    case "typewriterReveal":
      return <SequentialTypewriter title={title} message={message} />;
    case "envelopeReveal":
      return <EnvelopeReveal>{children}</EnvelopeReveal>;
    case "dramaticReveal":
      return <DramaticReveal>{children}</DramaticReveal>;
    default:
      return <>{children}</>;
  }
}

function SequentialTypewriter({ title, message }: { title: string; message: string }) {
  const [titleDone, setTitleDone] = useState(false);
  const handleTitleComplete = useCallback(() => setTitleDone(true), []);

  useEffect(() => {
    setTitleDone(false);
  }, [title, message]);

  return (
    <div className="text-center">
      <h1 className="font-display text-3xl font-bold leading-tight sm:text-4xl">
        <TypewriterText text={title} speedMs={72} onComplete={handleTitleComplete} />
      </h1>
      {titleDone && message && (
        <p className="mt-4 text-base leading-relaxed opacity-80">
          <TypewriterText key={`${title}-${message}`} text={message} speedMs={50} />
        </p>
      )}
    </div>
  );
}

function CountdownReveal({ children }: { children: ReactNode }) {
  const [step, setStep] = useState(3);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (revealed) return;

    const timer = window.setInterval(() => {
      setStep((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          setRevealed(true);
          return 1;
        }
        return current - 1;
      });
    }, 850);

    return () => window.clearInterval(timer);
  }, [revealed]);

  if (revealed) {
    return <>{children}</>;
  }

  return (
    <div className="mx-auto flex max-w-sm flex-col items-center justify-center gap-4 rounded-[30px] border border-white/55 bg-white/35 px-8 py-10 text-center shadow-[0_24px_70px_-42px_rgba(83,28,52,0.66)] backdrop-blur-xl">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-white/55 text-[rgb(var(--accent-strong))] shadow-sm" aria-hidden>
        ✦
      </div>
      <div className="font-display text-7xl font-bold leading-none text-[rgb(var(--text))]">
        {step}
      </div>
      <p className="max-w-xs text-sm font-medium leading-6 text-[rgb(var(--subtext)/0.76)]">
        Hold on. Something lovely is opening.
      </p>
    </div>
  );
}
